import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { MDBModalRef } from '../../../app/shared/services/mdb-modal-stub.service';
import { IProjDocUpload } from '../../shared/interfaces/document.Interface';
import { IWorkflowActionData } from '../../shared/interfaces/workflowAction.interface';
import { Enums } from '../../shared/models/enums';
import { ModalService } from '../../shared/services/modal.service';
import { ParentChildCommunicationService } from '../../shared/services/parent-child-communication.service';
import { ProjectDocumentCommentModalComponent } from '../documentComment/projectLevel/project-documentComment-modal.component';
import { WorkflowActionService } from '../services/workflowAction.service';
import { DesignDocumentCommentModalComponent } from './designLevel/design-documentComment-modal.component';
import { MeasureDocumentCommentModalComponent } from './measureLevel/measure-documentComment-modal.component';
import { MissingDocumentsModalComponent } from './modal-missing-documents/missing-documents-modal.component';

@Injectable()
export class ProjectDocumentCommentCommonService {
    modalRef: MDBModalRef;
    confirmationStatus = Enums.confirmationStatus;

    private callbackProjDOcUploadModalClose = new Subject<any>(); // Source
    eventCallbackProjDocuUploadModalClose$ = this.callbackProjDOcUploadModalClose.asObservable(); // Stream

    projDocUpload: IProjDocUpload;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private modalService: ModalService,
        private parentChildCommunicationService: ParentChildCommunicationService,
        private workflowActionService: WorkflowActionService
    ) {
        this.parentChildCommunicationService.on('projectDecision').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
            (data) => this.projectDecisionAction(data));
    }

    setDefaults() {

    }

    // Show Project Document Comment

    showProjDocumentComment(projDocUpload: IProjDocUpload, overlayWidth?: any) {
        this.projDocUpload = projDocUpload;
        let projDocUploadDesign = projDocUpload;
        const modalOptions = {
            class: overlayWidth ? 'modal-fluid modal-full-height modal-right scrollableContent calculator'
            : 'modal-fluid modal-full-height modal-right scrollableContent calculator overlay90',
            containerClass: 'right',
            ignoreBackdropClick: true,
            keyboard: false,
            data: {
                projDocUploadDesign: projDocUploadDesign,
                projDocUpload: projDocUpload,
            }
        };

        this.modalRef = this.modalService.openModal(ProjectDocumentCommentModalComponent, modalOptions);

        this.modalRef.content.action.subscribe((result: any) => {
            if (result.status === this.confirmationStatus.ok) {
                this.callbackProjDOcUploadModalClose.next(result);
            }
        });
    }

    // Measure level
    showMeasureDocumentComment(projDocUpload: IProjDocUpload, overlayWidth?: any) {
        const modalOptions = {
            class: overlayWidth ? 'modal-fluid modal-full-height modal-right scrollableContent overlay90'
            : 'modal-fluid modal-full-height modal-right scrollableContent calculator',
            containerClass: 'right',
            ignoreBackdropClick: true,
            keyboard: false,
            data: {
                projDocUpload: projDocUpload,
            }
        };

        this.modalRef = this.modalService.openModal(MeasureDocumentCommentModalComponent, modalOptions);

        this.modalRef.content.action.subscribe((result: any) => {
            if (result.status === this.confirmationStatus.ok) {
                this.callbackProjDOcUploadModalClose.next(result);
            }
        });
    }

    // Design level
    showDesignDocumentComment(projDocUpload: IProjDocUpload, overlayWidth?: any) {
        const modalOptions = {
            class: overlayWidth ? 'modal-fluid modal-full-height modal-right scrollableContent overlay90'
                : 'modal-fluid modal-full-height modal-right scrollableContent calculator',
            containerClass: 'right',
            ignoreBackdropClick: true,
            keyboard: false,
            data: {
                projDocUpload: projDocUpload,
            }
        };

        this.modalRef = this.modalService.openModal(DesignDocumentCommentModalComponent, modalOptions);

        this.modalRef.content.action.subscribe((result: any) => {
            if (result.status === this.confirmationStatus.ok) {
                this.callbackProjDOcUploadModalClose.next(result);
            }
        });
    }

    //Missing Documents
    showMissisngDocumentComment(overlayWidth?: any) {
        const modalOptions = {
            class: overlayWidth ? 'modal-fluid modal-full-height modal-right scrollableContent overlay90' : 'modal-fluid modal-full-height modal-right scrollableContent calculator',
            containerClass: 'right',
            ignoreBackdropClick: true,
            keyboard: false,
            data: {
                heading: '',
                // content: 'Are you sure you want to change Stage?',
                okButton: 'INSERT',
                cancelButton: 'CLEAR ALL',
            }
        };

        this.modalRef = this.modalService.openModal(MissingDocumentsModalComponent, modalOptions);

        this.modalRef.content.action.subscribe((result: any) => {
            if (result.status == this.confirmationStatus.ok) {
                this.callbackProjDOcUploadModalClose.next(result);
            }
        });
    }

    projectDecisionAction(data) {
        if (this.modalRef) {
            this.modalRef.hide();
        }

        if (data.decision === 'approveThroughRef') {
            if (data.role === Enums.Role.Auditor) {
                this.workflowActionService.auditApproveThroughReference(this.getWorkflowActionData(data.projectData, Enums.Role.Auditor));
            }
            else if (data.role === Enums.Role.Reviewer) {
                this.workflowActionService.reviewApproveThroughReference(this.getWorkflowActionData(data.projectData, Enums.Role.Reviewer));
            }
        }
        else if (data.decision === 'approveOrRejectProject') {
            let isApproved = data.approvalStatus === Enums.projectApprovalStatus.approved;
            if (data.role === Enums.Role.Auditor) {
                this.workflowActionService.approveOrRejectProjectAudit(isApproved,
                    this.getWorkflowActionData(data.projectData, Enums.Role.Auditor));
            }
            else if (data.role === Enums.Role.Reviewer) {
                this.workflowActionService.approveOrRejectProjectReview(data.approvalStatus,
                    this.getWorkflowActionData(data.projectData, Enums.Role.Reviewer));
            }
            else if (data.role === Enums.Role.CertifierAdmin) {
                this.workflowActionService.approveOrRejectProjectCertification(isApproved, this.getWorkflowActionData(data.projectData, Enums.Role.CertifierAdmin));
            }
        }
        else if (data.decision === 'sendForAudit') {
            this.workflowActionService.sendForAudit(this.getWorkflowActionData(data.projectData, Enums.Role.Client));
        }
    }

    private getWorkflowActionData(projectData, role): IWorkflowActionData {      
        const resolvedSubprojectName = this.resolveSubprojectName(projectData);
        console.log('[ProjectDecision] resolved subproject name', {
            projectID: projectData?.projectID,
            projectDetailId: projectData?.projectDetailId,
            projectName: projectData?.projectName,
            subprojectName: projectData?.subprojectName,
            resolvedSubprojectName
        });

        let workFlowActionData: IWorkflowActionData = {
            projectId: projectData.projectID,
            masterProjectId: projectData.masterProjectId,
            masterProjectName: projectData.masterProjectName,
            subprojectName: resolvedSubprojectName,
            subprojectStage: projectData.projectStage,
            currentCycle: projectData.currentCycleNumber,
            projectDetailId: projectData.projectDetailId,
            subprojectStatus: projectData.projectStatus,
            role: role,
            createdBy: projectData.createdBy,
            comment: '',
            fromDashboard: projectData.fromDashboard ? projectData.fromDashboard : false,
            versionNumber: projectData.versionNumber,
            toolMajorVersion: projectData.toolMajorVersion,
            toolType: (projectData.toolMajorVersion.toString().includes('3')) ? projectData.toolName : ''
        }

        return workFlowActionData;
    }

    private resolveSubprojectName(projectData: any): string {
        const directName = projectData?.projectName || projectData?.subprojectName || projectData?.name || projectData?.masterProjectName;
        if (directName) {
            return directName;
        }

        if (!projectData?.jsonData) {
            return '';
        }

        try {
            const parsed = typeof projectData.jsonData === 'string' ? JSON.parse(projectData.jsonData) : projectData.jsonData;
            return parsed?.ProjectName || parsed?.projectName || '';
        } catch {
            return '';
        }
    }
}
